'use client';

import Link from '~/(tenant)/_components/tenant-link';
import Image from 'next/image';

import { User } from 'lucide-react';

import { publicStorageUrl } from '@kit/shared/storage';
import { Trans } from '@kit/ui/trans';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import {
  HorizontalCardScroller,
  SectionHeader,
} from '~/(tenant)/_components/native/_primitives';

import type { NativeCoach } from '~/(tenant)/_lib/server/load-native-coaches';

interface CoachesSectionProps {
  /**
   * Coaches prefetched server-side and threaded down from the page.
   * Server prefetch is required because the browser anon client has no
   * SELECT grant on `public.trainers` (see load-native-coaches.ts).
   */
  coaches: NativeCoach[];
}

export function CoachesSection({ coaches }: CoachesSectionProps) {
  const t = useNativeTokens();

  if (coaches.length === 0) return null;

  return (
    <section style={{ paddingTop: 32 }}>
      <SectionHeader
        eyebrow={<Trans i18nKey="common:nativeHome.theTeam" defaults="The team" />}
        title={<Trans i18nKey="coaches:title" defaults="Our coaches" />}
        action={{
          label: <Trans i18nKey="common:seeAll" defaults="All" />,
          href: '/coaches',
        }}
      />

      <HorizontalCardScroller gap={20}>
        {coaches.map((coach) => (
          <Link
            key={coach.id}
            href={`/coaches#coach-${coach.id}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              flexShrink: 0,
              width: 92,
              textDecoration: 'none',
              color: t.foreground,
            }}
          >
            <div
              style={{
                position: 'relative',
                width: 84,
                height: 84,
                borderRadius: '50%',
                overflow: 'hidden',
                backgroundColor: t.line,
              }}
            >
              {coach.picture_url ? (
                <Image
                  src={publicStorageUrl(coach.picture_url)}
                  alt={coach.name}
                  fill
                  className="object-cover"
                  unoptimized
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: t.muted,
                  }}
                >
                  <User size={32} strokeWidth={1.2} />
                </div>
              )}
            </div>
            <span
              style={{
                fontFamily: t.fontDisplay,
                fontSize: 14,
                lineHeight: 1.2,
                textAlign: 'center',
                color: t.foreground,
                width: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {coach.name}
            </span>
          </Link>
        ))}
      </HorizontalCardScroller>
    </section>
  );
}
